export class Debounce {
  #global;
  #isInProgress = false;
  #resolves = [];
  #debounceId = null;
  #context;
  #callback;

  /**
   * @param {Object} config
   * @param {Window} config.global
   */
  constructor({ global }) {
    this.#global = global;
  }

  /**
   * @param callback
   * @param context
   * @returns {function(...[*]): Promise<unknown>}
   */
  create(callback, context = this.#global) {
    this.#context = context;
    this.#callback = callback;

    const debounceCallback = (...args) => {
      return new Promise((resolve) => {
        this.#clear();
        this.#isInProgress = true;
        this.#resolves.push(() => resolve());
        this.#debounceId = this.#global.requestAnimationFrame(() => this.#flush(...args));
      });
    };

    debounceCallback.clear = () => this.#clear();
    debounceCallback.flush = (...args) => this.#flush(...args);
    debounceCallback.callImmediate = (...args) => this.#callImmediate(...args);
    return debounceCallback;
  }

  /**
   * @param args
   */
  #callImmediate(...args) {
    this.#callback.bind(this.#context)(...args);
  }

  /**
   * @param args
   */
  #flush(...args) {
    if (!this.#isInProgress) {
      return;
    }

    this.#callImmediate(...args);
    this.#resolves.forEach(resolve => {
      resolve();
    });
    this.#resolves = [];
    this.#clear();
  }

  #clear() {
    if (!this.#isInProgress) {
      return;
    }

    this.#global.cancelAnimationFrame(this.#debounceId);
    this.#debounceId = null;
    this.#isInProgress = false;
  }
}
